import type { ReactNode } from 'react';

/**
 * IBCS-conformant tooltip template used across all charts.
 * Always shows: main value + optional delta + note row.
 */
interface TooltipRow {
    label: string;
    value: ReactNode;
    valueClass?: string;
}

interface IbcsTooltipProps {
    title: string;
    rows: TooltipRow[];
    note?: string;
}

export default function IbcsTooltip({ title, rows, note }: IbcsTooltipProps) {
    return (
        <div className="bg-white border border-gray-300 rounded-md shadow-lg p-3 text-xs min-w-[180px]">
            <div className="font-semibold text-gray-900 mb-2 pb-1 border-b border-gray-200">
                {title}
            </div>
            <div className="space-y-1">
                {rows.map((r, i) => (
                    <div key={i} className="flex justify-between gap-3">
                        <span className="text-gray-600">{r.label}</span>
                        <span className={`font-mono tabular-nums ${r.valueClass ?? 'text-gray-900'}`}>
                            {r.value}
                        </span>
                    </div>
                ))}
            </div>
            {note && <div className="text-gray-500 mt-2 pt-1 border-t border-gray-200 italic">{note}</div>}
        </div>
    );
}

const CURRENCY_SYMBOL: Record<string, string> = {
    EUR: '€', USD: '$', GBP: '£', JPY: '¥', CNY: '¥',
};

/** Render a currency suffix — symbol for known ones, code otherwise. */
function currencySuffix(code: string): string {
    return CURRENCY_SYMBOL[code] ?? code;
}

/** Format a money amount with given currency in human-readable short form (k/M). */
export function formatMoney(n: number | null | undefined, currency: string = 'EUR', short = true): string {
    if (n == null) return '—';
    const suffix = currencySuffix(currency);
    if (!short) return `${Math.round(n).toLocaleString('de-CH')} ${suffix}`;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M ${suffix}`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}k ${suffix}`;
    return `${Math.round(n)} ${suffix}`;
}

/** Format an EUR amount. Backward-compat wrapper around {@link formatMoney}. */
export function formatEur(n: number | null | undefined, short = true): string {
    return formatMoney(n, 'EUR', short);
}

export function formatPct(n: number | null | undefined): string {
    if (n == null) return '—';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
}

export function formatFcy(n: number | null | undefined, code: string): string {
    if (n == null) return '—';
    return `${Math.round(n).toLocaleString('de-CH')} ${code}`;
}
